import * as THREE from 'three';
import { box, cyl, mat, type LandmarkDef } from './common';
import { landmarkModel, windowGlass } from './model';

/**
 * landmarks/london.ts — London's landmarks. All hand-built low-poly Blender models
 * (glTF in public/models/landmarks/) except bigBen, which keeps the procedural
 * Houses of Parliament group with the Elizabeth Tower (Big Ben) Blender model
 * dropped into it. Drive-through gateways ('span') scale by width to clear the road.
 */

/** GS7 — the Palace's Gothic bay grammar. Victoria Tower was a BARE 13x30x13 box
 *  and the riverfront's windows were 12 flat lit slabs, so both masses read as
 *  unfinished beside the M41 scenery blocks (which wear full facade detail) and
 *  beside Big Ben's own model. Each bay is a recessed dark reveal + two mullioned
 *  sashes + a pointed head; every part is ONE InstancedMesh across the whole
 *  group (3 draw calls for ~54 bays — a mesh per part would have cost ~200).
 *  `ry` turns a bay authored on a +Z wall onto the tower's ±X faces. */
interface GothicBay {
  x: number;
  y: number;
  z: number;
  ry: number;
  w: number;
  h: number;
}

function gothicBays(bays: GothicBay[]): THREE.Object3D[] {
  const reveal = new THREE.BoxGeometry(1, 1, 0.38);
  const sash = new THREE.BoxGeometry(1, 1, 0.22);
  const head = new THREE.ConeGeometry(0.62, 0.74, 4);
  head.rotateY(Math.PI / 4); // a flat side faces the wall; per-instance scale then flattens it
  const build = (
    geo: THREE.BufferGeometry,
    color: number,
    emissive: number,
    xs: { p: THREE.Vector3; q: THREE.Quaternion; s: THREE.Vector3 }[],
  ): THREE.InstancedMesh => {
    const m = new THREE.InstancedMesh(geo, mat(color, emissive), xs.length);
    const t = new THREE.Matrix4();
    xs.forEach((x, i) => m.setMatrixAt(i, t.compose(x.p, x.q, x.s)));
    m.instanceMatrix.needsUpdate = true;
    return m;
  };
  const R: { p: THREE.Vector3; q: THREE.Quaternion; s: THREE.Vector3 }[] = [];
  const S: typeof R = [];
  const H: typeof R = [];
  for (const b of bays) {
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, b.ry, 0));
    const n = new THREE.Vector3(Math.sin(b.ry), 0, Math.cos(b.ry)); // outward wall normal
    const t = new THREE.Vector3(Math.cos(b.ry), 0, -Math.sin(b.ry)); // along the wall
    const at = (u: number, dy: number, d: number) =>
      new THREE.Vector3(b.x, b.y, b.z).addScaledVector(t, u).addScaledVector(n, d).setY(b.y + dy);
    R.push({ p: at(0, 0, 0.1), q, s: new THREE.Vector3(b.w, b.h, 1) });
    const sw = (b.w - 0.46) / 2; // two sashes with a 0.22 stone mullion between them
    for (const u of [-(sw / 2 + 0.11), sw / 2 + 0.11])
      S.push({ p: at(u, -0.06, 0.24), q, s: new THREE.Vector3(sw, b.h - 0.6, 1) });
    H.push({
      p: at(0, b.h / 2 + b.w * 0.3, 0.14),
      q,
      s: new THREE.Vector3(b.w * 1.02, b.w * 0.92, b.w * 0.32),
    });
  }
  return [
    build(reveal, 0x2c2b2a, 0, R), // the recessed reveal is what tames the glow
    build(sash, 0xfff0b0, 0xffcf66, S), // same warm class as the rest of the city
    build(head, 0xd9cca6, 0, H),
  ];
}

function bigBen(): THREE.Group {
  const g = new THREE.Group();
  const stone = 0xcdbd92; // warm Portland limestone
  const stoneLite = 0xd9cca6;
  const trim = 0xb09a63;
  const ironDark = 0x3a4150; // cast-iron spire
  const gold = 0xd8b23a;

  // Houses of Parliament — long Gothic riverfront block (x -14..26, z ±5.5).
  g.add(box(40, 13, 11, stone, 6, 6.5, 0));
  g.add(box(41, 1.4, 12, trim, 6, 13.3, 0)); // parapet band
  for (let i = -3; i <= 9; i++) g.add(cyl(0, 0.6, 3, 4, stoneLite, 6 + i * 3.1, 14.6, 0)); // roofline pinnacles

  // Victoria Tower (the big square one) at the +X end (x 17.5..30.5, z ±6.5).
  g.add(box(13, 30, 13, stone, 24, 15, 0));
  g.add(box(13.6, 1.6, 13.6, trim, 24, 30.4, 0));
  // GS7 buttress ribs — corner piers + a mid-face pilaster on all four sides, so
  // the tower stops reading as one flat cliff of stone.
  for (const sx of [-1, 1])
    for (const sz of [-1, 1]) g.add(box(2.4, 30.6, 2.4, stoneLite, 24 + sx * 6, 15.3, sz * 6));
  for (const s of [-1, 1]) {
    g.add(box(1.1, 30, 1.8, stoneLite, 24 + s * 6.55, 15, 0));
    g.add(box(1.8, 30, 1.1, stoneLite, 24, 15, s * 6.55));
  }
  for (const cx of [-5.5, 5.5]) for (const cz of [-5.5, 5.5]) g.add(cyl(0, 1, 4, 4, stoneLite, 24 + cx, 31.8, cz));
  g.add(cyl(0.2, 0.2, 3, 6, gold, 24, 33.5, 0)); // flag mast
  g.add(box(2.6, 1.4, 0.15, 0xd23357, 24, 34.4, 0)); // the flag flying

  // GS7 — the bay grid. Riverfront bays stop at x 15.2 so none of them ends up
  // standing inside Victoria Tower (the old slab loop ran to x 33.2 and put lit
  // rectangles across the tower's foot).
  const bays: GothicBay[] = [];
  for (let i = 0; i <= 8; i++) {
    const x = -12 + i * 3.4;
    for (const y of [4.4, 9.4]) {
      bays.push({ x, y, z: 5.5, ry: 0, w: 2.4, h: 3.4 });
      bays.push({ x, y, z: -5.5, ry: Math.PI, w: 2.4, h: 3.4 });
    }
  }
  const faces: [number, number, number][] = [
    [24, 6.5, 0],
    [24, -6.5, Math.PI],
    [30.5, 0, Math.PI / 2],
    [17.5, 0, -Math.PI / 2],
  ];
  for (const [fx, fz, ry] of faces)
    for (const y of [6.5, 12.5, 18.5, 24.5])
      for (const u of [-3.2, 3.2])
        bays.push({
          x: fx + Math.cos(ry) * u,
          y,
          z: fz - Math.sin(ry) * u,
          ry,
          w: 2.6,
          h: 4,
        });
  g.add(...gothicBays(bays));

  // The Central Tower's octagonal spire over the middle of the Palace.
  g.add(box(6, 6, 6, stone, 6, 16, -1));
  g.add(cyl(2.2, 3.2, 7, 8, stoneLite, 6, 22.5, -1));
  g.add(cyl(0, 2.2, 6, 8, ironDark, 6, 29, -1));
  // The riverfront terrace balustrade at the water's edge.
  g.add(box(42, 1.1, 0.8, trim, 6, 1.6, 6.4));

  // Elizabeth Tower (Big Ben) — hand-built low-poly Blender model anchoring the -X end.
  const tower = landmarkModel('models/landmarks/big_ben.glb', { height: 64 });
  tower.position.x = -16;
  g.add(tower);
  return g;
}

export const LONDON_LANDMARKS: Record<string, LandmarkDef> = {
  bigBen: { build: bigBen, placement: 'side', offset: 10 },
  londonEye: { build: () => landmarkModel('models/landmarks/london_eye.glb', { height: 42 }), placement: 'side', offset: 30 },
  // GS6: overcast-day city — the blown-white 'win' slabs become dark tinted
  // glass with a faint warm glow (the Apollo recipe via windowGlass).
  stPauls: { build: () => landmarkModel('models/landmarks/st_pauls.glb', { height: 38, ...windowGlass('win', 0.5) }), placement: 'side', offset: 13 },
  shard: { build: () => landmarkModel('models/landmarks/shard.glb', { height: 96 }), placement: 'side', offset: 32 },
  // London round: rebuilt as a real lathe (the old model was a stack of mint
  // cones that read as a chess piece). Smooth 30 Sq Mary Axe profile — swelling
  // to its widest a third of the way up, tapering to the glass crown lens — in
  // tinted curtain-wall glass carrying the SIX dark light-well spirals. Those
  // spirals are the whole read at racing distance: a full crossed diagrid was
  // tried and MEASURED as argyle knitwear, so only a single fine descending
  // grid line survives. 62 → 74 so it clears The City zone's ~55u towers (it
  // was buried in the block at 62). Only `gk_plinthstone` hits texturing.ts
  // (the plaza deck); the glass/steel names miss every regex on purpose.
  gherkin: { build: () => landmarkModel('models/landmarks/gherkin.glb', { height: 74 }), placement: 'side', offset: 18 },
  // London round: the 'bblue' cyan wedges are GONE from the model itself (the
  // GS6 hideMats was a stopgap) and the White Tower now flies four rippling
  // St George pennants off real staffs on the turret caps. Slab windows re-cut
  // to narrow lights in Blender, and the ashlar is tinted COOL so the shared
  // lm_stone albedo lands on pale Kentish ragstone instead of brown rubble.
  // NOTE: 'stoneL' is now unique to this model — St Paul's was renamed to a
  // portland/plaster role and tower_bridge.glb was rebuilt (see texturing.ts's
  // stoneL comment, which is stale by one round).
  towerOfLondon: {
    build: () =>
      landmarkModel('models/landmarks/tower_of_london.glb', { height: 24, ...windowGlass('win', 0.5) }),
    placement: 'side',
    offset: 11,
  },
  // London round: tower_bridge.glb was rebuilt from scratch (scripted Blender,
  // 4.4k tris / 232 kB — the photogrammetry scan it replaces was a 21k-tri
  // shard-covered stump that only survived at 100+ units). Real structure now:
  // two ashlar towers with corner turrets + spires, the TWIN high-level
  // walkways spanning overhead, and proper straight-link suspension chains
  // sweeping down to shore pylons with vertical hangers over the side spans.
  //
  // GATEWAY CLEARANCE (the Mumbai gatewayOfIndia precedent — a ROAD-RELATIVE
  // width, never a fixed scale). MEASURED on the exported GLB: the narrowest
  // deck-level gap (the pier caps, inboard of the tower shafts) is 38.20% of
  // the model's total X width. So width = (road width + 8) / 0.382 leaves ~4u
  // of clear air outside each guardrail at ANY track half-width. At hw=18:
  // 115 wide, 48 tall, opening ±22 over a ±18 road, walkway soffit ~24 up.
  //
  // Materials: the two `*gothic*` names are deliberate — they hit texturing.ts's
  // STONE role so the towers wear real ashlar; every other name misses every
  // regex and ships its authored colour (Jubilee-blue steel, lead roofs, dark
  // glass with the overcast-day warm whisper). Hence NO skipTexturing here.
  towerBridge: {
    build: (hw) =>
      landmarkModel('models/landmarks/tower_bridge.glb', { width: (2 * hw + 8) / 0.382 }),
    placement: 'span',
  },
};
